import { TwoUIProgressView } from "@amx/ux-twoui-telemetry";
import { Depths, IStackStyles, ProgressIndicator, Stack as FluentUiStack } from "@fluentui/react";
import React, { forwardRef, useState } from "react";
import { BaseProps, validateProps } from "../../../shared/props";
import { Label } from "../../Label/Label";
import { Stack } from "../../Stacks/Stack/Stack";
import { DetailsListInternal, DetailsListInternalProps } from "../DetailsList/DetailsList";
import { GroupedListInternal } from "../GroupedList/GroupedList";
import { GroupedListInternalProps } from "../GroupedList/GroupedList.types";
import { ListInternal, ListInternalProps, ListRef } from "../List/List";
import { LinkListFooter } from "../ListFooter/LinkListFooter/LinkListFooter";
import { ListItemCanvas } from "../ListItems/shared/ListItemCanvas";
import { listItemPadding } from "../ListItems/shared/styles";
import { ListBorderRadius, ListWrapperCollapseProps } from "../shared/props";
import { childrenAsElementArray, onRenderCell } from "../shared/utils";
import { ShowMoreButton, ShowMoreButtonProps } from "./ShowMoreButton";

const getStyles = (shadow?: boolean, borderRadius?: ListBorderRadius, roundedCorners?: boolean, customHeight?: string | number): IStackStyles => ({
    root: {
        boxShadow: shadow ? Depths.depth4 : undefined,
        borderRadius: roundedCorners ? borderRadius : undefined,
        overflow: roundedCorners ? "hidden" : undefined,
        height: customHeight
    }
});

export type ListWrapperProps = ListWrapperCollapseProps & ListWrapperTypeProps | (ListWrapperCollapseProps & ListWrapperTypeProps & DetailsListInternalProps);

interface ListWrapperTypeProps extends BaseProps {
    readonly listType: "List" | "GroupedList" | "DetailsList";
}

export const defaultInitialListLength = 5;

export const ListWrapper: React.FC<ListWrapperProps & React.RefAttributes<ListRef>> = forwardRef<ListRef, ListWrapperProps>(({
    listType,
    label,
    header,
    footer,
    placeholder,
    children,
    shadow,
    roundedCorners,
    borderRadius = "8px",
    operationData,
    showDefaultHeaderDivider = true,
    ...otherProps
}, ref) => {
    const [isShowingAll, setIsShowingAll] = useState(otherProps["defaultExpanded"] ?? false);
    let baseProps: BaseProps;
    let computedInitialListLength: number;
    let computedLabelText = label?.text;
    let showMoreProps: ShowMoreButtonProps | undefined;
    let computedListItems = childrenAsElementArray(children);
    if (otherProps.isAlwaysExpanded) {
        computedInitialListLength = 0;

        // Trim otherProps so that we're only passing BaseProps to the Fluent component
        const { isAlwaysExpanded, ...retrievedBaseProps } = otherProps;
        baseProps = retrievedBaseProps;
    } else {
        computedInitialListLength = otherProps.initialListLength || defaultInitialListLength;

        // If there are more list items than the initialListLength, add total number to label
        // and create an expand/collapse footer
        if (computedInitialListLength > 0 && computedListItems.length > computedInitialListLength) {
            computedLabelText = `${computedLabelText} (${computedListItems.length})`;

            const collapseButtonProps = isShowingAll ? otherProps.showLessButtonProps : otherProps.showMoreButtonProps;

            showMoreProps = {
                ...collapseButtonProps,
                onClick: () => setIsShowingAll(!isShowingAll)
            };
        }

        computedListItems = isShowingAll ? computedListItems : computedListItems.slice(0, computedInitialListLength);

        // Trim otherProps so that we're only passing BaseProps to the Fluent component
        const { isAlwaysExpanded, initialListLength, showMoreButtonProps, showLessButtonProps, ...retrievedBaseProps } = otherProps;
        baseProps = retrievedBaseProps;
    }

    // No divider needed for the last item, in the case of DetailsList, we do not know the index last item as the items may be sorted
    if (computedListItems.length > 0 && listType !== "DetailsList") {
        const lastItem = computedListItems[computedListItems.length - 1];
        computedListItems[computedListItems.length - 1] = React.createElement(lastItem.type, { ...lastItem.props, showDivider: false });
    }

    // Add gridcell role for DetailList items for accessibility compliance
    if (listType === "DetailsList") {
        computedListItems = computedListItems.map((item) => {
            return React.createElement(item.type, { ...item.props, cellRole: "gridcell" });
        });
    }

    validateProps("ListWrapper", baseProps);
    const getListContent = (disabled?: boolean) => {
        const listProps: ListInternalProps | GroupedListInternalProps = {
            "aria-labelledby": label?.id,
            ...baseProps,
            disabled,
            children: computedListItems
        };

        const detailsListProps: DetailsListInternalProps = {
            "aria-labelledby": label?.id,
            ...baseProps,
            disabled,
            children: computedListItems,
            headerColumns: (otherProps as DetailsListInternalProps).headerColumns,
            ariaLabelForListHeader: (otherProps as DetailsListInternalProps).ariaLabelForListHeader,
            checkButtonAriaLabel: (otherProps as DetailsListInternalProps).checkButtonAriaLabel,
            onHeaderChange: (otherProps as DetailsListInternalProps).onHeaderChange,
            announcedSortMessage: (otherProps as DetailsListInternalProps).announcedSortMessage
        };

        const createdList = () => {
            switch (listType) {
                case "List":
                    return <ListInternal {...listProps} ref={ref} />;
                case "DetailsList":
                    return <DetailsListInternal {...detailsListProps} />;
                default:
                    return <GroupedListInternal showDivider={showDefaultHeaderDivider} {...listProps} />;
            }
        };

        return (
            <>
                {createdList()}
                {showMoreProps && <ShowMoreButton {...showMoreProps} />}
            </>
        );
    };

    const customHeight = listType === "DetailsList" ? (otherProps as DetailsListInternalProps).customHeight : undefined;

    const getComputedList = (disabled?: boolean) => (
        <FluentUiStack styles={getStyles(shadow, borderRadius, roundedCorners, customHeight)}>
            {label && <Label id={label.id}>{computedLabelText}</Label>}
            {disabled && (
                <div style={{ position: "absolute", width: "100%", zIndex: 1 }}>
                    <ProgressIndicator styles={{ itemProgress: { padding: "0px" } }} />
                </div>
            )}
            {header && onRenderCell(
                (
                    <ListItemCanvas showDivider={showDefaultHeaderDivider} removeRole={true} data-is-focusable={false} >
                        <Stack padding={listItemPadding}>
                            {disabled ? React.cloneElement(header, { menuDisabled: disabled, showDivider: showDefaultHeaderDivider }) : header}
                        </Stack>
                    </ListItemCanvas>
                ), otherProps.listItemBackgroundColor, undefined, undefined, showDefaultHeaderDivider)
            }
            {(placeholder && computedListItems.length === 0) ? placeholder : getListContent(disabled)}
            {footer && <LinkListFooter {...footer} />}
        </FluentUiStack>
    );

    if (operationData) {
        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                failedView={getComputedList()}
                finishedView={getComputedList()}
                updatingView={getComputedList(true)}
            />
        );
    } else {
        return getComputedList();
    }
});
